import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, MapPin, QrCode, Clock, Users, Bell, FileText, 
  Camera, CheckCircle, BarChart3, Calendar, AlertTriangle,
  Smartphone, Globe, Lock, Zap, ChevronRight, Menu, X,
  Building, Route, ClipboardList, UserCheck, Timer, 
  MessageSquare, Download, Eye, Settings, Map, Radio,
  Layers, Activity, TrendingUp, PieChart, Target, Award,
  Building2, UserPlus, ScanLine, LineChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { api } from '../services/api';

export default function Landing() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    adminName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Listen for PWA install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstallGuide(true);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password Mismatch",
        description: "Passwords do not match"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Weak Password",
        description: "Password must be at least 6 characters"
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.createOrganization({
        name: formData.organizationName,
        email: formData.email, // Organization email (same as admin email for simplicity)
        admin_name: formData.adminName,
        admin_email: formData.email,
        admin_password: formData.password,
        phone: formData.phone || null
      });
      
      toast({
        title: "Account Created!",
        description: "Your organization has been registered. You can now log in."
      });
      
      setShowSignup(false);
      navigate('/dashboard');
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Registration Failed",
        description: error.message || "Failed to create organization"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Clock,
      title: "Smart Clock In/Out",
      description: "GPS-verified attendance tracking with offline support. Guards can clock in even without internet."
    },
    {
      icon: QrCode,
      title: "QR Checkpoint Scanning",
      description: "Deploy QR codes at patrol points. Guards scan to verify presence with timestamp and location."
    },
    {
      icon: MapPin,
      title: "Live GPS Tracking",
      description: "Real-time location monitoring of all guards on interactive maps powered by OpenStreetMap."
    },
    {
      icon: Camera,
      title: "Geotagged Photo Reports",
      description: "Guards upload photos with automatic GPS coordinates and timestamps for verification."
    },
    {
      icon: AlertTriangle,
      title: "Incident Reporting",
      description: "Quick incident logging with severity levels, photos, and instant supervisor notifications."
    },
    {
      icon: Calendar,
      title: "Advanced Scheduling",
      description: "Create recurring shifts, assign guards to sites, and manage complex rotation patterns."
    },
    {
      icon: Bell,
      title: "Push Notifications",
      description: "Instant alerts for shift reminders, incidents, and important updates to all team members."
    },
    {
      icon: FileText,
      title: "Timesheet Management",
      description: "Automatic timesheet generation with approval workflows and time adjustment capabilities."
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Comprehensive reports on attendance, patrol completion, incidents, and guard performance."
    },
    {
      icon: Building,
      title: "Multi-Site Management",
      description: "Manage multiple locations from a single dashboard with site-specific settings and staff."
    },
    {
      icon: Users,
      title: "Role-Based Access",
      description: "Four-tier system: Super Admin, Admin, Supervisor, and Guard with granular permissions."
    },
    {
      icon: Route,
      title: "Patrol Route Tracking",
      description: "Visualize guard patrol paths on maps, verify checkpoint coverage and timing."
    },
    {
      icon: ClipboardList,
      title: "Activity Timeline",
      description: "Unified view of all activities: clock-ins, scans, photos, and incidents in chronological order."
    },
    {
      icon: UserCheck,
      title: "Join Request System",
      description: "Guards can request to join your organization using invite codes. Approve or reject with one click."
    },
    {
      icon: Timer,
      title: "Shift Reminders",
      description: "Automated 10-minute pre-shift notifications ensure guards are always prepared."
    },
    {
      icon: Download,
      title: "PDF QR Code Export",
      description: "Generate printable PDFs with all checkpoint QR codes for easy deployment at sites."
    },
    {
      icon: Eye,
      title: "Real-Time Monitoring",
      description: "Watch guard activities as they happen with live updates on dashboard."
    },
    {
      icon: Settings,
      title: "Organization Settings",
      description: "Customize your organization profile, manage invite codes, and configure preferences."
    },
    {
      icon: Map,
      title: "Google Maps Integration",
      description: "Direct links to Google Maps for any geotagged activity for easy navigation."
    },
    {
      icon: Radio,
      title: "Offline-First Design",
      description: "Full functionality without internet. Data syncs automatically when connection restores."
    },
    {
      icon: Layers,
      title: "Multi-Tenant Architecture",
      description: "Each organization's data is completely isolated and secure from others."
    },
    {
      icon: Activity,
      title: "Guard Performance Metrics",
      description: "Track individual guard statistics: punctuality, patrol completion rate, incident response."
    },
    {
      icon: TrendingUp,
      title: "CSV Export",
      description: "Export patrol data, scans, and photos to CSV for external analysis and reporting."
    },
    {
      icon: PieChart,
      title: "Attendance Reports",
      description: "Generate detailed attendance reports by guard, site, or date range."
    },
    {
      icon: Target,
      title: "Checkpoint Verification",
      description: "Ensure guards visit all required checkpoints with verification timestamps."
    },
    {
      icon: Award,
      title: "7-Day Schedule View",
      description: "Guards see their full week schedule with shift details and days off clearly marked."
    },
    {
      icon: MessageSquare,
      title: "Supervisor Notes",
      description: "Add notes to shifts and checkpoints for special instructions to guards."
    },
    {
      icon: Lock,
      title: "Secure Authentication",
      description: "JWT-based authentication with password hashing and role-based access control."
    }
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-slate-800">TheSecurityGuardApp</span>
                <span className="hidden sm:inline text-xs text-slate-500 ml-2">Security Management</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#how-it-works" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">How It Works</a>
              <Button variant="ghost" onClick={handleInstallClick} className="text-slate-700 gap-1.5" data-testid="install-app-btn">
                <Download className="w-4 h-4" />
                Install App
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-700">
                Log In
              </Button>
              <Button onClick={() => setShowSignup(true)} className="bg-blue-600 hover:bg-blue-700">
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4 space-y-3">
            <a href="#features" className="block py-2 text-slate-600 hover:text-blue-600">Features</a>
            <a href="#how-it-works" className="block py-2 text-slate-600 hover:text-blue-600">How It Works</a>
            <Button variant="outline" onClick={handleInstallClick} className="w-full gap-1.5">
              <Download className="w-4 h-4" />
              Install App
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
              Log In
            </Button>
            <Button onClick={() => setShowSignup(true)} className="w-full bg-blue-600 hover:bg-blue-700">
              Get Started
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Modern Security Guard Management Platform
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Manage Your Security Team
              <span className="text-blue-600"> With Confidence</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              The complete solution for security companies. Track guards in real-time, 
              verify patrols with QR codes, manage schedules, and generate reports — all from one platform.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                onClick={() => setShowSignup(true)}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 h-14 px-8 text-lg"
              >
                Start Your Organization
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto h-14 px-8 text-lg"
              >
                Sign In to Dashboard
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Works Offline</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Real-Time Tracking</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>Multi-Site Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>PWA - Install on Any Device</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get your security operation up and running in minutes
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                icon: Building2,
                title: "Create Organization",
                description: "Sign up and set up your security company profile in seconds"
              },
              {
                step: "02",
                icon: UserPlus,
                title: "Add Your Team",
                description: "Invite supervisors and guards using secure invite codes"
              },
              {
                step: "03",
                icon: ScanLine,
                title: "Deploy Checkpoints",
                description: "Print and place QR codes at patrol points across your sites"
              },
              {
                step: "04",
                icon: LineChart,
                title: "Monitor & Report",
                description: "Track everything in real-time and generate comprehensive reports"
              }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
                  <item.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Manage Security Operations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive suite of tools designed specifically for security companies
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Security Operations?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Join security companies already using TheSecurityGuardApp to streamline their operations, 
            improve accountability, and deliver better service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => setShowSignup(true)}
              className="w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50 h-14 px-8 text-lg"
            >
              Create Your Organization
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto border-white text-white hover:bg-white/10 h-14 px-8 text-lg"
            >
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">TheSecurityGuardApp</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <span>© 2025 TheSecurityGuardApp. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4">
              <Globe className="w-5 h-5" />
              <span className="text-sm">Available Worldwide</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Get Started Modal — Choice: Join or Create */}
      {showSignup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Get Started</h2>
                    <p className="text-sm text-slate-500">Choose how you'd like to join</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSignup(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Option 1: Join an existing organization */}
              <button
                onClick={() => { setShowSignup(false); navigate('/register'); }}
                className="w-full text-left p-5 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
                data-testid="get-started-join"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-green-200 transition-colors">
                    <UserPlus className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">Join an Organization</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      I have an invite code from my employer and want to join as a guard or supervisor
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 mt-1 shrink-0 group-hover:text-blue-500 transition-colors" />
                </div>
              </button>

              {/* Option 2: Create a new organization */}
              <button
                onClick={() => { setShowSignup(false); navigate('/register?mode=create'); }}
                className="w-full text-left p-5 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
                data-testid="get-started-create"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-200 transition-colors">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">Create New Organization</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      I'm a security company owner and want to set up my organization
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 mt-1 shrink-0 group-hover:text-blue-500 transition-colors" />
                </div>
              </button>

              <p className="text-center text-sm text-slate-500 pt-2">
                Already have an account?{' '}
                <button 
                  type="button"
                  onClick={() => { setShowSignup(false); navigate('/login'); }}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Log In
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Install Guide Modal */}
      {showInstallGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Install App</h2>
                    <p className="text-sm text-slate-500">Add to your home screen</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInstallGuide(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <p className="text-slate-600 text-sm">
                Install TheSecurityGuardApp on your device for quick access, offline support, and a native app experience.
              </p>

              {/* iOS Instructions */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">Safari (iPhone / iPad)</span>
                </h4>
                <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> button (square with arrow)</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong> to confirm</li>
                </ol>
              </div>

              {/* Android / Chrome Instructions */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">Chrome (Android / Desktop)</span>
                </h4>
                <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
                  <li>Tap the <strong>three-dot menu</strong> (top right)</li>
                  <li>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Install"</strong> to confirm</li>
                </ol>
              </div>

              <p className="text-xs text-slate-400 text-center">
                Works on all modern browsers and devices
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
